import { useEffect } from 'react';
import { useAppContext } from '../AppContext/AppContext';
import { ROUTE_FLOW } from '../constants';
import { reportException } from '../tools/sentry';
import { OrderFlow } from '../types';
import { useCurrentRoute } from './useCurrentRoute';

/**
 * Reverse lookup: maps each route to the flows it belongs to.
 * Built once at module load time from ROUTE_FLOW.
 *
 * Routes shared across all flows (DATA_ENTRY, ORDER_COMPLETE) will list every flow —
 * those are never a mismatch. Routes appearing in only one flow (BATTERY_OPTIMIZATION,
 * SELF_CONSUMPTION_PLUS, DIRECT_MARKETING, MODULES_STORE) will only list that flow.
 */
const ROUTE_TO_FLOWS = Object.entries(ROUTE_FLOW).reduce<Record<string, OrderFlow[]>>(
  (acc, [flow, routes]) => {
    routes.forEach((route) => {
      acc[route] = [...(acc[route] ?? []), flow as OrderFlow];
    });
    return acc;
  },
  {},
);

/**
 * Global hook that detects when the app's `orderFlow` state doesn't match the
 * current route. This catches cases where the mobile app deep-links to a route
 * without setting the correct flow (e.g. navigating to /battery-optimization
 * while orderFlow is still "energyDynamic").
 *
 * Call once in AppRoutes — no per-page handling required.
 */
export const useOrderFlowStateMismatch = () => {
  const { orderFlow } = useAppContext();
  const currentRoute = useCurrentRoute();

  useEffect(() => {
    if (!orderFlow) return;

    const expectedFlows = ROUTE_TO_FLOWS[currentRoute];
    if (!expectedFlows) return; // route outside any flow (e.g. /login, /maintenance)

    if (!expectedFlows.includes(orderFlow)) {
      reportException(new Error('Order flow mismatch for current route'), {
        tag: 'order-flow-state',
        context: {
          currentRoute,
          detectedOrderFlow: orderFlow,
          expectedFlows,
        },
      });
    }
  }, [currentRoute, orderFlow]);
};
